import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Plus,
  RefreshCw,
  Send,
  Eye,
  Heart,
  MessageSquare,
  Calendar,
  Pin,
  Edit2,
  Trash2,
  Clock,
  Users,
  TrendingUp,
  Image as ImageIcon,
  Video,
  X,
  Upload,
  Link2
} from "lucide-react";
import { useRef } from "react";

const PORTAL_OPTIONS = [
  { value: "investor", label: "Investor Portal" },
  { value: "team", label: "Team Portal" },
  { value: "advocate", label: "Advocate Portal" },
  { value: "support", label: "Support Portal" },
  { value: "network", label: "Network Portal" },
  { value: "host", label: "Host Portal" },
];

const CATEGORY_OPTIONS = [
  { value: "product", label: "Product" }, { value: "expansion", label: "Expansion" },
  { value: "milestone", label: "Milestone" },
  { value: "hair", label: "Hair" }, { value: "makeup", label: "Makeup" },
  { value: "nails", label: "Nails" }, { value: "lashes", label: "Lashes" },
  { value: "events", label: "Events" }, { value: "instructors", label: "Instructors" },
  { value: "culinary", label: "Culinary" }, { value: "wellness", label: "Wellness" },
  { value: "travel", label: "Travel" }, { value: "dispensary", label: "Dispensary" },
  { value: "style", label: "Retail" },
];

const UPDATE_TYPES = [
  { value: "general", label: "General Update" },
  { value: "milestone", label: "Milestone" },
  { value: "feature", label: "New Feature" },
  { value: "announcement", label: "Announcement" },
  { value: "insight", label: "Insight" }
];

// Create/Edit Update Dialog
function UpdateDialog({ isOpen, onClose, update, onSave }) {
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    summary: "",
    update_type: "general",
    visibility: "all",
    target_portals: [],
    target_categories: [],
    target_orgs: [],
    is_pinned: false,
    allow_reactions: true,
    allow_comments: false,
    is_published: true,
    display_date: "",
    hide_date: false,
    media: [],
    cta_text: "",
    cta_url: ""
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (update) {
      setFormData({ ...formData, ...update, media: update.media || [], target_portals: update.target_portals || [], target_categories: update.target_categories || [], target_orgs: update.target_orgs || [] });
    } else {
      setFormData({
        title: "", content: "", summary: "", update_type: "general",
        visibility: "all", target_portals: [], target_categories: [], target_orgs: [],
        is_pinned: false, allow_reactions: true,
        allow_comments: false, is_published: true, display_date: "", hide_date: false, media: [], cta_text: "", cta_url: ""
      });
    }
  }, [update, isOpen]);

  // Load orgs for targeting
  useEffect(() => {
    if (isOpen) {
      const token = localStorage.getItem("session_token");
      fetch(`${API}/admin/organizations`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [])
        .then(d => setOrgs(Array.isArray(d) ? d : d.organizations || []))
        .catch(() => {});
    }
  }, [isOpen]);

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("Max 20MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "phygital/updates");
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/cms/upload`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd
      });
      if (res.ok) {
        const data = await res.json();
        const isVideo = file.type.startsWith("video/");
        setFormData(prev => ({
          ...prev,
          media: [...prev.media, { url: data.secure_url || data.url, type: isVideo ? "video" : "image", name: file.name }]
        }));
        toast.success("Media uploaded!");
      }
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const removeMedia = (idx) => {
    setFormData(prev => ({ ...prev, media: prev.media.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.content) {
      toast.error("Title and content are required");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const url = update 
        ? `${API}/admin/platform-updates/${update.update_id}`
        : `${API}/admin/platform-updates`;
      
      const res = await fetch(url, {
        method: update ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(update ? "Update edited" : "Update shared!");
        onSave();
        onClose();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
      }
    } catch (err) {
      toast.error("Error saving update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            {update ? "Edit Update" : "Share an Update"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label>Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="What's the headline?"
            />
          </div>

          <div>
            <Label>Content *</Label>
            <Textarea
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Share your update..."
              rows={6}
            />
          </div>

          <div>
            <Label>Summary (for preview)</Label>
            <Input
              value={formData.summary}
              onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))}
              placeholder="Short preview text"
            />
          </div>

          {/* Date display settings */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Display Date (optional)</Label>
              <Input
                type="date"
                className="h-8 text-xs"
                value={formData.display_date || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, display_date: e.target.value }))}
              />
              <p className="text-[10px] text-gray-400 mt-0.5">Override the auto-generated date</p>
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Switch
                checked={formData.hide_date || false}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, hide_date: checked }))}
              />
              <Label className="text-xs">Hide date</Label>
            </div>
          </div>

          {/* Media Attachments */}
          <div>
            <Label className="mb-2 block">Media Attachments</Label>
            {formData.media?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.media.map((m, idx) => (
                  <div key={idx} className="relative group w-20 h-20 rounded-lg overflow-hidden border bg-gray-50">
                    {m.type === "video" ? (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                        <Video className="w-6 h-6 text-gray-400" />
                      </div>
                    ) : (
                      <img src={m.url} alt="" className="w-full h-full object-cover" />
                    )}
                    <button onClick={() => removeMedia(idx)} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <input ref={fileInputRef} type="file" accept="image/*,video/*" onChange={handleMediaUpload} className="hidden" />
            <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              {uploading ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
              {uploading ? "Uploading..." : "Add Image or Video"}
            </Button>
          </div>

          {/* CTA Button */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>CTA Button Text</Label>
              <Input value={formData.cta_text || ""} onChange={(e) => setFormData(prev => ({ ...prev, cta_text: e.target.value }))} placeholder="e.g. Learn More" />
            </div>
            <div>
              <Label>CTA URL</Label>
              <Input value={formData.cta_url || ""} onChange={(e) => setFormData(prev => ({ ...prev, cta_url: e.target.value }))} placeholder="https://..." />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Type</Label>
              <Select 
                value={formData.update_type} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, update_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UPDATE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 pt-5">
              <Switch
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))}
              />
              <Label className="text-sm">{formData.is_published ? "Publish Now" : "Save as Draft"}</Label>
            </div>
          </div>

          {/* Single unified portal/audience targeting */}
          <div className="space-y-3 border rounded-lg p-3 bg-gray-50/50">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Targeting</p>
            
            {/* Tags (content categories) */}
            <div>
              <Label className="text-xs">Tags (filter categories on portal)</Label>
              <p className="text-xs text-gray-400 mb-1">Select tags so users can filter updates on their portal</p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {CATEGORY_OPTIONS.map(c => {
                  const active = formData.target_categories?.includes(c.value);
                  return (
                    <button key={c.value} type="button"
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${active ? 'bg-purple-100 border-purple-400 text-purple-700' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        target_categories: active ? prev.target_categories.filter(v => v !== c.value) : [...(prev.target_categories || []), c.value]
                      }))}
                      data-testid={`target-cat-${c.value}`}
                    >{c.label}</button>
                  );
                })}
              </div>
            </div>

            {/* Portal selection */}
            <div>
              <Label className="text-xs">Which portals see this?</Label>
              <p className="text-xs text-gray-400 mb-1">Leave empty to show on all portals</p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {PORTAL_OPTIONS.map(p => {
                  const active = formData.target_portals?.includes(p.value);
                  return (
                    <button key={p.value} type="button"
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${active ? 'bg-blue-100 border-blue-400 text-blue-700' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        target_portals: active ? prev.target_portals.filter(v => v !== p.value) : [...(prev.target_portals || []), p.value]
                      }))}
                      data-testid={`target-portal-${p.value}`}
                    >{p.label}</button>
                  );
                })}
              </div>
            </div>

            {/* Target Categories */}
            <div>
              <Label className="text-xs">Target Categories</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {CATEGORY_OPTIONS.map(c => {
                  const active = formData.target_categories?.includes(c.value);
                  return (
                    <button key={c.value} type="button"
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${active ? 'bg-purple-100 border-purple-400 text-purple-700' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        target_categories: active ? prev.target_categories.filter(v => v !== c.value) : [...(prev.target_categories || []), c.value]
                      }))}
                      data-testid={`target-cat-${c.value}`}
                    >{c.label}</button>
                  );
                })}
              </div>
            </div>

            {/* Target Orgs */}
            {orgs.length > 0 && (
              <div>
                <Label className="text-xs">Target Organizations</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {orgs.map(o => {
                    const orgId = o.org_id || o.name;
                    const active = formData.target_orgs?.includes(orgId);
                    return (
                      <button key={orgId} type="button"
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${active ? 'bg-green-100 border-green-400 text-green-700' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          target_orgs: active ? prev.target_orgs.filter(v => v !== orgId) : [...(prev.target_orgs || []), orgId]
                        }))}
                        data-testid={`target-org-${orgId}`}
                      >{o.name}</button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">Pin this update</p>
                <p className="text-xs text-gray-500">Keep at top of feed</p>
              </div>
              <Switch
                checked={formData.is_pinned}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_pinned: checked }))}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">Allow reactions</p>
                <p className="text-xs text-gray-500">Let people react</p>
              </div>
              <Switch
                checked={formData.allow_reactions}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, allow_reactions: checked }))}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">Allow comments</p>
                <p className="text-xs text-gray-500">Let people respond</p>
              </div>
              <Switch
                checked={formData.allow_comments}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, allow_comments: checked }))}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : update ? "Update" : "Share Update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UpdatesSection() {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const fetchUpdates = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/platform-updates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUpdates(Array.isArray(data) ? data : data.updates || []);
      }
    } catch (err) {
      console.error("Failed to fetch updates:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/updates/analytics/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
    }
  }, []);

  useEffect(() => {
    fetchUpdates();
    fetchAnalytics();
  }, [fetchUpdates, fetchAnalytics]);

  const handleDelete = async (updateId) => {
    if (!window.confirm("Delete this update?")) return;

    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/platform-updates/${updateId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Update deleted");
        fetchUpdates();
        fetchAnalytics();
      }
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const openEdit = (update) => {
    setEditingUpdate(update);
    setShowDialog(true);
  };

  const openCreate = () => {
    setEditingUpdate(null);
    setShowDialog(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const getReactionCount = (reactions) => {
    if (!reactions) return 0;
    return Object.values(reactions).reduce((a, b) => a + b, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Updates & Outreach</h2>
          <p className="text-gray-500">Share updates with your community</p>
        </div>
        <Button onClick={openCreate} className="bg-gradient-to-r from-purple-500 to-pink-500">
          <Send className="w-4 h-4 mr-2" />
          Share an Update
        </Button>
      </div>

      {/* Analytics */}
      {analytics && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <p className="text-2xl font-bold">{analytics.total_updates}</p>
              <p className="text-sm text-gray-500">Total Updates</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{analytics.total_views}</p>
              <p className="text-sm text-gray-500">Total Views</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <p className="text-2xl font-bold text-pink-600">{analytics.total_reactions}</p>
              <p className="text-sm text-gray-500">Reactions</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <p className="text-2xl font-bold text-green-600">{analytics.views_today}</p>
              <p className="text-sm text-gray-500">Views Today</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Updates List */}
      <div className="space-y-4">
        {updates.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Send className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No updates yet</p>
              <p className="text-sm text-gray-400 mt-1">Share your first update with the community</p>
              <Button onClick={openCreate} variant="outline" className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Create First Update
              </Button>
            </CardContent>
          </Card>
        ) : (
          updates.map(update => (
            <Card key={update.update_id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {update.is_pinned && (
                        <Badge className="bg-amber-500">
                          <Pin className="w-3 h-3 mr-1" />
                          Pinned
                        </Badge>
                      )}
                      {!update.is_published && (
                        <Badge variant="secondary">Draft</Badge>
                      )}
                      <Badge variant="outline">
                        {update.target_portals?.length > 0
                          ? `${update.target_portals.length} portal${update.target_portals.length > 1 ? 's' : ''}`
                          : "All Portals"
                        }
                      </Badge>
                      <Badge variant="outline" className="capitalize">{update.update_type}</Badge>
                    </div>
                    
                    <h3 className="font-semibold text-lg">{update.title}</h3>
                    <p className="text-gray-600 mt-1 line-clamp-2">{update.summary || update.content}</p>
                    
                    {/* Admin-only targeting tags */}
                    {(update.target_portals?.length > 0 || update.target_categories?.length > 0 || update.target_orgs?.length > 0) && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(update.target_portals || []).map(p => (
                          <Badge key={p} className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">{PORTAL_OPTIONS.find(o => o.value === p)?.label || p}</Badge>
                        ))}
                        {(update.target_categories || []).map(c => (
                          <Badge key={c} className="bg-purple-100 text-purple-700 border-0 text-[10px] capitalize">{CATEGORY_OPTIONS.find(o => o.value === c)?.label || c}</Badge>
                        ))}
                        {(update.target_orgs || []).map(o => (
                          <Badge key={o} className="bg-blue-100 text-blue-700 border-0 text-[10px]">{typeof o === 'object' ? o.name : o.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Badge>
                        ))}
                      </div>
                    )}
                    
                    {/* Media preview - 16:9 aspect ratio */}
                    {update.media?.length > 0 && (
                      <div className="flex gap-2 mt-3 overflow-x-auto">
                        {update.media.map((m, idx) => (
                          <div key={idx} className="w-48 aspect-video rounded-lg overflow-hidden border bg-gray-50 shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={(e) => { e.stopPropagation(); window.open(m.url, '_blank'); }}>
                            {m.type === "video" ? (
                              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                <Video className="w-6 h-6 text-gray-400" />
                              </div>
                            ) : (
                              <img src={m.url} alt="" className="w-full h-full object-cover" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* CTA Button */}
                    {update.cta_text && update.cta_url && (
                      <a href={update.cta_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors">
                        {update.cta_text}
                        <Link2 className="w-3.5 h-3.5" />
                      </a>
                    )}
                    
                    <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                      {!update.hide_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(update.display_date || update.created_at)}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {update.view_count || 0} views
                      </span>
                      {update.allow_reactions && (
                        <span className="flex items-center gap-1">
                          <Heart className="w-4 h-4" />
                          {getReactionCount(update.reactions)} reactions
                        </span>
                      )}
                    </div>
                    
                    {/* Emoji Reaction Bar */}
                    {update.allow_reactions && (
                      <EmojiReactionBar 
                        reactions={update.reactions || {}}
                        updateId={update.update_id}
                        onReact={fetchUpdates}
                      />
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(update)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700"
                      onClick={() => handleDelete(update.update_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Dialog */}
      <UpdateDialog
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
        update={editingUpdate}
        onSave={() => { fetchUpdates(); fetchAnalytics(); }}
      />
    </div>
  );
}


const EMOJI_OPTIONS = [
  { emoji: "❤️", key: "heart" },
  { emoji: "🔥", key: "fire" },
  { emoji: "👏", key: "clap" },
  { emoji: "💡", key: "idea" },
  { emoji: "🎉", key: "celebrate" },
  { emoji: "💜", key: "purple_heart" },
  { emoji: "✨", key: "sparkle" },
  { emoji: "🙌", key: "praise" }
];

function EmojiReactionBar({ reactions, updateId, onReact }) {
  const [showPicker, setShowPicker] = useState(false);

  const handleReact = async (emojiKey) => {
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/updates/${updateId}/react`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reaction: emojiKey })
      });
      onReact();
    } catch {
      // silent fail for reactions
    }
    setShowPicker(false);
  };

  const activeReactions = EMOJI_OPTIONS.filter(e => (reactions[e.key] || 0) > 0);

  return (
    <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid={`reactions-${updateId}`}>
      {activeReactions.map(({ emoji, key }) => (
        <button
          key={key}
          onClick={() => handleReact(key)}
          className="flex items-center gap-1 px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-sm transition-colors"
        >
          <span>{emoji}</span>
          <span className="text-xs font-medium text-gray-600">{reactions[key]}</span>
        </button>
      ))}
      
      <div className="relative">
        <button
          onClick={() => setShowPicker(!showPicker)}
          className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full text-sm transition-colors"
          data-testid={`add-reaction-${updateId}`}
        >
          +
        </button>
        {showPicker && (
          <div className="absolute bottom-full left-0 mb-2 bg-white shadow-lg rounded-xl p-2 flex gap-1 border z-50">
            {EMOJI_OPTIONS.map(({ emoji, key }) => (
              <button
                key={key}
                onClick={() => handleReact(key)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg text-lg transition-colors"
                title={key}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
